/**
 * Medicines & products: every product the pharmacy has, classified or not,
 * and the pharmacist's review of the ones nobody has classified yet.
 *
 * Classifying never guesses. A medicine gets the class the pharmacist picks —
 * there is no default — and a reason is kept with every decision. Until then
 * an item is shown, priced and counted, but sold nowhere in the pharmacy.
 *
 * Medicines already classified are reviewed the same way, many at once, by a
 * registered pharmacist: a class given wholesale is put right item by item.
 */

import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Download, Pencil, Pill } from 'lucide-react';
import {
  RX_CLASSES,
  RX_CLASS_LABELS,
  STORAGE_REQUIREMENTS,
  STORAGE_REQUIREMENT_LABELS,
  type CatalogueStatusKey,
} from '@cenox/shared';
import { api, downloadFile } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { money } from '../../lib/format';
import { Badge, Button, Card, EmptyState, ErrorState, Input, Modal, Pagination, Select, Spinner } from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { usePharmacy, usePharmacyWrite } from './data';
import { ErrorLine, RxBadge, Table, Td } from './parts';

export interface CatalogueRow {
  id: string;
  name: string;
  sku: string | null;
  barcode: string | null;
  categoryId: string | null;
  categoryName: string | null;
  costPrice: string | null;
  sellingPrice: string | null;
  onHand: number;
  /** Counted, batch/expiry not yet recorded: stock held, never sold. */
  countedPending?: number;
  /** On hand plus counted stock pending its batches. */
  recordedStock?: number;
  status: CatalogueStatusKey;
  rxClass: string | null;
  tracksBatches: boolean | null;
}
interface CatalogueList {
  data: CatalogueRow[];
  meta: { page: number; pageSize: number; total: number };
  counts: Record<CatalogueStatusKey | 'all', number>;
}

export const STATUS_LABELS: Record<CatalogueStatusKey, string> = {
  medicine: 'Medicine',
  non_medicine: 'Cosmetic / retail',
  unclassified: 'Awaiting classification',
};

export function StatusBadge({ row }: { row: Pick<CatalogueRow, 'status' | 'rxClass'> }): ReactNode {
  if (row.status === 'medicine' && row.rxClass) return <RxBadge rxClass={row.rxClass} />;
  if (row.status === 'non_medicine') return <Badge tone="info">Cosmetic / retail</Badge>;
  return <Badge tone="warning">Awaiting classification</Badge>;
}

const linkFor = (row: CatalogueRow) =>
  row.status === 'unclassified' ? `/pharmacy/stock/products/${row.id}` : `/pharmacy/stock/medicines/${row.id}`;

/** The whole catalogue: filters, pages, and — for a pharmacist — bulk review. */
export function CatalogueList({ branchId }: { branchId: string | null }): ReactNode {
  const { can, business } = useAuth();
  const path = useBusinessPath();
  const c = business?.currencyCode ?? 'NGN';
  const mayClassify = can('pharmacy:classify');
  const [status, setStatus] = useState<CatalogueStatusKey | ''>('');
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Map<string, CatalogueRow>>(new Map());
  const [reviewing, setReviewing] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<unknown>(null);
  const mayPrepareSheet = can('pharmacy:catalogue');

  async function downloadWorksheet() {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadFile(`/api/v1${path('/pharmacy/catalogue/worksheet?status=unclassified')}`, 'classification-worksheet.xlsx');
    } catch (error) {
      setDownloadError(error);
    } finally {
      setDownloading(false);
    }
  }

  const list = usePharmacy<CatalogueList>('/catalogue', {
    page,
    pageSize: 50,
    search: search || undefined,
    status: status || undefined,
    categoryId: categoryId || undefined,
    branchId: branchId ?? undefined,
  });
  const categories = useQuery({
    queryKey: ['products', path(''), 'categories'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }>>(path('/products/categories')),
  });
  const categoryOptions = (() => {
    const d = categories.data;
    return (Array.isArray(d) ? d : (d?.data ?? [])).slice().sort((a, b) => a.name.localeCompare(b.name));
  })();

  const counts = list.data?.counts;
  const tabs: Array<{ key: CatalogueStatusKey | ''; label: string }> = [
    { key: '', label: 'All' },
    { key: 'medicine', label: 'Medicines' },
    { key: 'non_medicine', label: 'Cosmetics & retail' },
    { key: 'unclassified', label: 'Awaiting classification' },
  ];
  const toggle = (row: CatalogueRow) =>
    setSelected((s) => {
      const next = new Map(s);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  const pageUnclassified = (list.data?.data ?? []).filter((r) => r.status === 'unclassified');
  const pageMedicines = (list.data?.data ?? []).filter((r) => r.status === 'medicine');
  // The header box selects what this view is about: medicines on the Medicines tab, else what awaits classification.
  const pageSelectable = status === 'medicine' ? pageMedicines : pageUnclassified;
  const chosen = [...selected.values()];
  const allUnclassified = chosen.length > 0 && chosen.every((r) => r.status === 'unclassified');
  const allMedicines = chosen.length > 0 && chosen.every((r) => r.status === 'medicine');

  return (
    <div>
      <div className="mb-3 flex flex-wrap gap-2" role="tablist" aria-label="Classification">
        {tabs.map((t) => (
          <button
            key={t.key || 'all'}
            type="button"
            role="tab"
            aria-selected={status === t.key}
            onClick={() => {
              setStatus(t.key);
              setPage(1);
            }}
            className={`rounded-full border px-3 py-1 text-sm ${status === t.key ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
          >
            {t.label}
            {counts && <span className="ml-1.5 tabular-nums text-slate-500">{counts[t.key || 'all']}</span>}
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Input
            aria-label="Search products"
            placeholder="Search name, SKU or scan a barcode"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>
        <div className="w-56">
          <Select
            aria-label="Category"
            value={categoryId}
            onChange={(e) => {
              setCategoryId(e.target.value);
              setPage(1);
            }}
            options={[{ value: '', label: 'Every category' }, ...categoryOptions.map((o) => ({ value: o.id, label: o.name }))]}
          />
        </div>
        {mayClassify && allUnclassified && <Button onClick={() => setReviewing(true)}>Classify {selected.size} selected</Button>}
        {mayClassify && allMedicines && <Button onClick={() => setReclassifying(true)}>Review class of {selected.size} selected</Button>}
        {mayClassify && selected.size > 0 && !allUnclassified && !allMedicines && (
          <span className="text-xs text-amber-700">Select only items awaiting classification, or only medicines.</span>
        )}
        {mayPrepareSheet && (counts?.unclassified ?? 0) > 0 && (
          <Button variant="secondary" icon={<Download className="size-4" />} loading={downloading} onClick={() => void downloadWorksheet()}>
            Download worksheet
          </Button>
        )}
      </div>
      {mayPrepareSheet && (counts?.unclassified ?? 0) > 0 && (
        <p className="mb-4 text-xs text-slate-500">
          Many items? Download the worksheet, fill in Medicine, Class and Storage for each, and upload it under{' '}
          <Link to="/pharmacy/settings/import" className="text-brand-700 underline">Import &amp; migration</Link> as a classification
          sheet. Nothing is written until a pharmacist approves it. The category column is only there to help you find things.
        </p>
      )}
      <ErrorLine error={downloadError} />

      <Card padded={false}>
        {list.isError ? (
          <div className="p-4">
            <ErrorState message="Could not load the catalogue." onRetry={() => void list.refetch()} />
          </div>
        ) : !list.data ? (
          <Spinner />
        ) : list.data.data.length === 0 ? (
          <EmptyState
            icon={<Pill className="size-8" />}
            title={search || status || categoryId ? 'Nothing matches' : 'No products yet'}
            description="Add medicines here, or import the catalogue from Settings → Import & migration."
          />
        ) : (
          <>
            <Table
              head={[
                mayClassify ? (
                  <input
                    key="all"
                    type="checkbox"
                    aria-label={status === 'medicine' ? 'Select every medicine on this page' : 'Select every unclassified item on this page'}
                    checked={pageSelectable.length > 0 && pageSelectable.every((r) => selected.has(r.id))}
                    onChange={(e) =>
                      setSelected((s) => {
                        const next = new Map(s);
                        for (const r of pageSelectable) {
                          if (e.target.checked) next.set(r.id, r);
                          else next.delete(r.id);
                        }
                        return next;
                      })
                    }
                  />
                ) : (
                  ''
                ),
                'Product',
                'SKU',
                'Barcode',
                'Category',
                'Cost',
                'Price',
                branchId ? 'Stock here' : 'Stock',
                'Status',
              ]}
              minWidth={980}
            >
              {list.data.data.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <Td>
                    {mayClassify && (r.status === 'unclassified' || r.status === 'medicine') && (
                      <input type="checkbox" aria-label={`Select ${r.name}`} checked={selected.has(r.id)} onChange={() => toggle(r)} />
                    )}
                  </Td>
                  <Td>
                    <Link to={linkFor(r)} className="font-medium text-brand-700 hover:underline">
                      {r.name}
                    </Link>
                  </Td>
                  <Td className="text-xs text-slate-600">{r.sku ?? '—'}</Td>
                  <Td className="text-xs text-slate-600">{r.barcode ?? '—'}</Td>
                  <Td className="text-xs text-slate-600">{r.categoryName ?? '—'}</Td>
                  <Td className="tabular-nums text-slate-600">{r.costPrice ? money(r.costPrice, c) : '—'}</Td>
                  <Td className="tabular-nums">{r.sellingPrice ? money(r.sellingPrice, c) : '—'}</Td>
                  <Td className="tabular-nums">
                    <StockCell row={r} />
                  </Td>
                  <Td>
                    <StatusBadge row={r} />
                  </Td>
                </tr>
              ))}
            </Table>
            <Pagination page={list.data.meta.page} pageSize={list.data.meta.pageSize} total={list.data.meta.total} onPageChange={setPage} />
          </>
        )}
      </Card>
      {reclassifying && (
        <ReclassifyModal
          items={chosen}
          onClose={() => setReclassifying(false)}
          onDone={() => {
            setSelected(new Map());
            setReclassifying(false);
          }}
        />
      )}
      {reviewing && (
        <ClassifyModal
          items={[...selected.values()]}
          onClose={() => setReviewing(false)}
          onDone={() => {
            setSelected(new Map());
            setReviewing(false);
          }}
        />
      )}
    </div>
  );
}

/**
 * The pharmacist's decision for one or many items: not a medicine, or a
 * medicine of the class they choose. One reason for the batch, kept with
 * every item in its classification history.
 */
export function ClassifyModal({
  items,
  onClose,
  onDone,
}: {
  items: CatalogueRow[];
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>>('post', '/catalogue/classify');
  const [decision, setDecision] = useState<'non_medicine' | 'medicine' | ''>('');
  const [rxClass, setRxClass] = useState('');
  const [storage, setStorage] = useState('');
  const [sameStorage, setSameStorage] = useState(false);
  const [baseUnitName, setBaseUnitName] = useState('unit');
  const [reason, setReason] = useState('');
  const many = items.length > 1;
  const ready =
    decision !== '' &&
    (decision === 'non_medicine' || (rxClass !== '' && storage !== '' && (!many || sameStorage))) &&
    reason.trim().length >= 5 &&
    baseUnitName.trim();
  return (
    <Modal open onClose={onClose} title={items.length === 1 ? `Classify ${items[0]!.name}` : `Classify ${items.length} products`} size="lg">
      <div className="space-y-4 text-sm">
        {items.length > 1 && (
          <ul className="max-h-32 overflow-y-auto rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600">
            {items.map((i) => (
              <li key={i.id}>{i.name}</li>
            ))}
          </ul>
        )}
        <fieldset className="space-y-2">
          <legend className="font-medium text-slate-800">What {items.length === 1 ? 'is it' : 'are they'}?</legend>
          <label className="flex items-center gap-2">
            <input type="radio" name="decision" checked={decision === 'non_medicine'} onChange={() => setDecision('non_medicine')} />
            Not a medicine — cosmetic, supplement, provision or other retail
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" name="decision" checked={decision === 'medicine'} onChange={() => setDecision('medicine')} />
            A medicine
          </label>
        </fieldset>
        {decision === 'medicine' && (
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Class"
              value={rxClass}
              onChange={(e) => setRxClass(e.target.value)}
              options={[{ value: '', label: 'Choose the class' }, ...RX_CLASSES.map((r) => ({ value: r, label: RX_CLASS_LABELS[r] }))]}
            />
            <Select
              label="Storage"
              value={storage}
              onChange={(e) => setStorage(e.target.value)}
              options={[
                { value: '', label: 'Choose the storage' },
                ...STORAGE_REQUIREMENTS.map((s) => ({ value: s, label: STORAGE_REQUIREMENT_LABELS[s] })),
              ]}
            />
            {many && (
              <label className="flex items-start gap-2 text-xs text-slate-700 sm:col-span-2">
                <input type="checkbox" className="mt-0.5" checked={sameStorage} onChange={(e) => setSameStorage(e.target.checked)} />
                Every one of these {items.length} items is stored this way. Classify cold-chain items (cool, fridge, freezer) on their
                own if they differ.
              </label>
            )}
            <p className="text-xs text-slate-500 sm:col-span-2">
              Medicines are tracked by batch and expiry; their stock is received with batches. Restricted medicines stay unsellable until the
              owner records the regulatory sign-off.
            </p>
          </div>
        )}
        <Input
          label="Selling unit"
          hint="What one of these is sold as: tablet, bottle, pack, tube. Its barcode and price stay as they are."
          value={baseUnitName}
          onChange={(e) => setBaseUnitName(e.target.value)}
        />
        <Input
          label="Reason"
          hint="Kept in each item's classification history, with your name and the date."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button
            loading={save.isPending}
            disabled={!ready}
            onClick={() =>
              save.mutate(
                {
                  reason: reason.trim(),
                  items: items.map((i) => ({
                    productId: i.id,
                    decision,
                    baseUnitName: baseUnitName.trim(),
                    ...(decision === 'medicine' ? { rxClass, storageRequirement: storage } : {}),
                  })),
                },
                { onSuccess: onDone },
              )
            }
          >
            Classify
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/**
 * A registered pharmacist's review of medicines already classified: the class
 * each one really is, or that it is not a medicine at all. Every item gets
 * the class chosen here; one reason, kept in each item's history.
 */
export function ReclassifyModal({
  items,
  onClose,
  onDone,
}: {
  items: CatalogueRow[];
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const save = usePharmacyWrite<Record<string, unknown>, { changed: number; unchanged: number }>('post', '/catalogue/reclassify');
  const [decision, setDecision] = useState('');
  const [reason, setReason] = useState('');
  const ready = decision !== '' && reason.trim().length >= 5;
  return (
    <Modal open onClose={onClose} title={`Review the class of ${items.length} medicine${items.length === 1 ? '' : 's'}`} size="lg">
      <div className="space-y-4 text-sm">
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-600">
          {items.map((i) => (
            <li key={i.id} className="flex justify-between gap-2">
              <span>{i.name}</span>
              <span className="text-slate-400">{i.rxClass ? RX_CLASS_LABELS[i.rxClass as keyof typeof RX_CLASS_LABELS] ?? i.rxClass : ''}</span>
            </li>
          ))}
        </ul>
        <Select
          label="Every item selected is"
          value={decision}
          onChange={(e) => setDecision(e.target.value)}
          options={[
            { value: '', label: 'Choose' },
            ...RX_CLASSES.map((r) => ({ value: r, label: RX_CLASS_LABELS[r] })),
            { value: 'non_medicine', label: 'Not a medicine — device, cosmetic, food or other retail' },
          ]}
        />
        {decision === 'non_medicine' && (
          <p className="text-xs text-slate-500">
            It stops being tracked by batch. Stock counted for it is then added from Counted stock — batch/expiry pending, as counted.
          </p>
        )}
        <Input
          label="Reason"
          hint="Kept in each item's classification history, with your name and registration."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
        <p className="text-xs text-slate-500">Only a pharmacist whose current registration is on file can make this review.</p>
        <ErrorLine error={save.error} />
        {save.data ? (
          <div className="flex items-center justify-between gap-2">
            <p className="text-emerald-700">
              {save.data.changed} changed{save.data.unchanged ? `, ${save.data.unchanged} already of that class` : ''}.
            </p>
            <Button onClick={onDone}>Close</Button>
          </div>
        ) : (
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button
              loading={save.isPending}
              disabled={!ready}
              onClick={() => save.mutate({ reason: reason.trim(), items: items.map((i) => ({ productId: i.id, decision })) })}
            >
              Save review
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** One unclassified product: what is on file, and the pharmacist's review. */
export function ProductReviewPage(): ReactNode {
  const { id = '' } = useParams();
  const { can, business } = useAuth();
  const c = business?.currencyCode ?? 'NGN';
  const item = usePharmacy<CatalogueRow>(`/catalogue/${id}`);
  const [classifying, setClassifying] = useState(false);
  const [editing, setEditing] = useState(false);
  if (item.isError) return <ErrorState message="Could not load this product." onRetry={() => void item.refetch()} />;
  if (!item.data) return <Spinner />;
  const r = item.data;
  return (
    <div className="space-y-5">
      <Link to="/pharmacy/stock" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="size-4" aria-hidden /> Medicines & products
      </Link>
      <PageHeader
        title={r.name}
        description={STATUS_LABELS[r.status]}
        action={
          <div className="flex flex-wrap gap-2">
            {can('pharmacy:catalogue') && (
              <Button variant="secondary" icon={<Pencil className="size-4" />} onClick={() => setEditing(true)}>
                Edit details
              </Button>
            )}
            {r.status === 'unclassified' && can('pharmacy:classify') && <Button onClick={() => setClassifying(true)}>Classify</Button>}
          </div>
        }
      />
      {r.status !== 'unclassified' && (
        <Card>
          <p className="text-sm text-slate-700">
            Classified. <Link to={`/pharmacy/stock/medicines/${r.id}`} className="text-brand-700 hover:underline">Open its pharmacy record</Link>.
          </p>
        </Card>
      )}
      <Card>
        <dl className="grid gap-3 text-sm sm:grid-cols-3">
          <Fact label="SKU" value={r.sku ?? '—'} />
          <Fact label="Barcode" value={r.barcode ?? '—'} />
          <Fact label="Category" value={r.categoryName ?? '—'} />
          <Fact label="Cost price" value={r.costPrice ? money(r.costPrice, c) : '—'} />
          <Fact label="Selling price" value={r.sellingPrice ? money(r.sellingPrice, c) : '—'} />
          <Fact label="Stock" value={<StockCell row={r} />} />
        </dl>
        <div className="mt-4">
          <StatusBadge row={r} />
        </div>
        {r.status === 'unclassified' && (
          <p className="mt-3 text-sm text-slate-600">
            Not sold anywhere in the pharmacy until a pharmacist says what it is. CenoX does not guess a class from the name.
          </p>
        )}
      </Card>
      {classifying && <ClassifyModal items={[r]} onClose={() => setClassifying(false)} onDone={() => setClassifying(false)} />}
      {editing && <EditProductModal product={r} onClose={() => setEditing(false)} />}
    </div>
  );
}

/**
 * Correct a product's own details: name, SKU, barcode, prices and category.
 *
 * Classified or not. It never says what the product is — classifying does
 * that — and never how much there is: stock, batches and expiry come in
 * through Add stock and its approval. Only what is changed is sent; the rest
 * is left exactly as it was. A barcode can be typed or scanned.
 */
export function EditProductModal({ product, onClose }: { product: CatalogueRow; onClose: () => void }): ReactNode {
  const path = useBusinessPath();
  const save = usePharmacyWrite<Record<string, unknown>, CatalogueRow>('patch', `/catalogue/${product.id}`);
  const categories = useQuery({
    queryKey: ['products', path(''), 'categories'],
    queryFn: () => api.get<{ data: Array<{ id: string; name: string }> } | Array<{ id: string; name: string }>>(path('/products/categories')),
  });
  const options = (() => {
    const d = categories.data;
    return (Array.isArray(d) ? d : (d?.data ?? [])).slice().sort((a, b) => a.name.localeCompare(b.name));
  })();
  const plainMoney = (v: string | null) => (v === null ? '' : String(Number(v)));
  const [f, setF] = useState({
    name: product.name,
    sku: product.sku ?? '',
    barcode: product.barcode ?? '',
    costPrice: plainMoney(product.costPrice),
    sellingPrice: plainMoney(product.sellingPrice),
    categoryId: product.categoryId ?? '',
  });
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const isMoney = (v: string) => v.trim() === '' || /^\d+(\.\d{1,2})?$/.test(v.trim());

  // Only what changed, so nothing untouched is rewritten.
  const changes: Record<string, unknown> = {};
  if (f.name.trim() && f.name.trim() !== product.name) changes.name = f.name.trim();
  if (f.sku.trim() !== (product.sku ?? '')) changes.sku = f.sku.trim() || null;
  if (f.barcode.trim().toUpperCase() !== (product.barcode ?? '')) changes.barcode = f.barcode.trim() || null;
  if (f.costPrice.trim() !== plainMoney(product.costPrice)) changes.costPrice = f.costPrice.trim() || null;
  if (f.sellingPrice.trim() !== plainMoney(product.sellingPrice)) changes.sellingPrice = f.sellingPrice.trim() || null;
  if (f.categoryId !== (product.categoryId ?? '')) changes.categoryId = f.categoryId || null;
  const problem = !f.name.trim()
    ? 'A product needs a name'
    : !isMoney(f.costPrice) || !isMoney(f.sellingPrice)
      ? 'Prices are amounts, e.g. 1250 or 1250.50'
      : null;
  const ready = !problem && Object.keys(changes).length > 0;

  return (
    <Modal open onClose={onClose} title={`Edit ${product.name}`} size="lg">
      <form
        aria-label="Edit product details"
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) save.mutate(changes, { onSuccess: onClose });
        }}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Name" required value={f.name} onChange={(e) => set('name', e.target.value)} />
          <Input label="SKU" value={f.sku} onChange={(e) => set('sku', e.target.value)} />
          <Input
            label="Barcode"
            value={f.barcode}
            placeholder="Type or scan"
            onChange={(e) => set('barcode', e.target.value)}
            // A scanner ends with Enter: that finishes the barcode, it does not save the form.
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.preventDefault();
            }}
          />
          <Select
            label="Category"
            value={f.categoryId}
            onChange={(e) => set('categoryId', e.target.value)}
            options={[{ value: '', label: 'No category' }, ...options.map((o) => ({ value: o.id, label: o.name }))]}
          />
          <Input label="Cost price" inputMode="decimal" value={f.costPrice} onChange={(e) => set('costPrice', e.target.value)} />
          <Input label="Selling price" inputMode="decimal" value={f.sellingPrice} onChange={(e) => set('sellingPrice', e.target.value)} />
        </div>
        <p className="text-xs text-slate-500">
          Stock, batches and expiry dates are entered through Add stock, and are approved before anything can be sold.
          {product.status === 'unclassified' ? ' Editing does not classify the product: it stays unsellable until a pharmacist classifies it.' : ''}
        </p>
        {problem && <p className="text-xs font-medium text-amber-700">{problem}</p>}
        <ErrorLine error={save.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={!ready} loading={save.isPending}>Save changes</Button>
        </div>
      </form>
    </Modal>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs text-slate-500">{label}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{value}</dd>
    </div>
  );
}

/**
 * Stock as the pharmacy holds it: on hand plus counted stock whose batch and
 * expiry are still to be recorded, marked as such — never shown as zero, and
 * never sold until its batches are recorded and released.
 */
function StockCell({ row }: { row: Pick<CatalogueRow, 'onHand' | 'countedPending'> }): ReactNode {
  const pending = row.countedPending ?? 0;
  if (pending <= 0) return <>{row.onHand}</>;
  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span>{row.onHand + pending}</span>
      <Badge tone="warning">Counted stock — batch/expiry pending: {pending}</Badge>
      {row.onHand !== 0 && <span className="text-xs text-slate-500">{row.onHand} with batches</span>}
    </span>
  );
}

